import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Spin, Table, Checkbox, Dropdown, Icon, Button, Menu, Drawer, Modal } from "ant-design-vue";
import { Column } from "ant-design-vue/lib";
import "ant-design-vue/dist/antd.less";
import { ILessonPlanTemplateModel, LessonPlanTemplateColumns } from "@/models/lesson-plan";
import LessonPlanTemplateState from "@/modules/lesson-plan-template";
import { LessonPlanLocale, CommonLocale } from "@/locales/localeid";
import { fmtMsg, GLUtil, ActionItem } from "commonui";
import PageTitle from "@/components/page-title/page-title.vue";
import TemplateEditorContent from "./components/editor/editor-content.vue";
import TemplateEditorContentType from "./components/editor/editor-content";
import { AccessLevel, LessonPlanTemplateActionStatus, MethodologyType } from "@/util/enum";
import { MethodologyTypeItemsForTemplate } from "@/util/constants";
import { Paths } from "@/util/paths";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import Grid from "@/components/wijmo-grid/grid/grid.vue";

@Component({
	components: {
		Grid,
		"a-spin": Spin,
		"a-table": Table,
		"a-checkbox": Checkbox,
		"a-dropdown": Dropdown,
		"a-icon": Icon,
		"a-button": Button,
		"a-menu": Menu,
		"a-menu-item": Menu.Item,
		"a-drawer": Drawer,
		PageTitle,
		ActionItem,
		TemplateEditorContent
	}
})
export default class TemplateList extends Vue {
	private lessonPlanTemplateModule = getModule(LessonPlanTemplateState);
	private displayTemplateEditor: boolean = false;
	private currentAction: LessonPlanTemplateActionStatus = LessonPlanTemplateActionStatus.Edit;
	public accessLevel = AccessLevel;

	get columns(): Column[] {
		return [
			{
				title: fmtMsg(LessonPlanLocale.LessonPlanTemplateColumnNameTitle),
				dataIndex: LessonPlanTemplateColumns.NameColumn,
				key: LessonPlanTemplateColumns.NameColumn,
				width: 250,
				scopedSlots: { customRender: "name" }
			},
			{
				title: fmtMsg(LessonPlanLocale.LessonPlanTemplateColumnMethodologyTitle),
				dataIndex: LessonPlanTemplateColumns.MethodologyColumn,
				key: LessonPlanTemplateColumns.MethodologyColumn,
				width: 120,
				scopedSlots: { customRender: "methodology" }
			},
			{
				title: fmtMsg(LessonPlanLocale.LessonPlanTemplateColumnDefaultTitle),
				dataIndex: LessonPlanTemplateColumns.DefaultColumn,
				key: LessonPlanTemplateColumns.DefaultColumn,
				width: 115,
				scopedSlots: { customRender: "default" }
			},
			{
				title: fmtMsg(LessonPlanLocale.LessonPlanTemplateColumnDeleteTitle),
				dataIndex: LessonPlanTemplateColumns.DeleteColumn,
				key: LessonPlanTemplateColumns.DeleteColumn,
				width: 45,
				scopedSlots: { customRender: "delete" }
			}
		];
	}

	get loading(): boolean {
		return this.lessonPlanTemplateModule.loading;
	}

	get lessonPlanLocale(): any {
		return LessonPlanLocale;
	}

	get commonLocale(): any {
		return CommonLocale;
	}

	get lessonPlanTemplateColumns(): any {
		return LessonPlanTemplateColumns;
	}

	get templates(): ILessonPlanTemplateModel[] {
		return this.lessonPlanTemplateModule.lessonPlanTemplates.map((template) => ({
			...template,
			methodologyText: this.methodologyItems.get(
				template[LessonPlanTemplateColumns.MethodologyColumn] as MethodologyType
			)
		}));
	}

	get lessonPlanTemplateFormTitle(): string {
		switch (this.currentAction) {
			case LessonPlanTemplateActionStatus.Edit:
				return fmtMsg(LessonPlanLocale.LessonPlanTemplateEditFormTitle);
			case LessonPlanTemplateActionStatus.Add:
			default:
				return fmtMsg(LessonPlanLocale.LessonPlanTemplateAddFormTitle);
		}
	}

	get lessonPlanTemplate(): any {
		return this.lessonPlanTemplateModule.currentLessonPlanTemplate;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	getLessonPlanTemplateEditorRouter(record: ILessonPlanTemplateModel): string {
		return GLUtil.pathStringify(Paths.EditLessonPlanTemplate, { templateId: record.id });
	}

	onCloseEditor(): void {
		this.displayTemplateEditor = false;
		(this.$refs.templateEditorContent as TemplateEditorContentType).onResetFormFieldsValue();
	}

	onDownloadStandardTemplate(event: any): void {
		this.lessonPlanTemplateModule.downloadBuildinLessonPlanTemplate();
	}

	onAddLessonPlanTemplate(event: any): void {
		// this.lessonPlanTemplateModule.setCurrentLessonPlanTemplate({});
		// this.currentAction = LessonPlanTemplateActionStatus.Add;
		// this.displayTemplateEditor = true;
		this.$router.push(Paths.AddLessonPlanTemplate);
	}

	onEditLessonPlanTemplate(templateId: string): void {
		this.lessonPlanTemplateModule.fetchLessonPlanTemplate({ templateId });
		this.currentAction = LessonPlanTemplateActionStatus.Edit;
		this.displayTemplateEditor = true;
	}

	get methodologyItems(): Map<MethodologyType, string> {
		return MethodologyTypeItemsForTemplate;
	}

	onSaveLessonPlanTemplate(): void {
		const success: (values: any) => Promise<void> = async (values) => {
			Object.keys(values).forEach((valueKey) => {
				this.lessonPlanTemplateModule.currentLessonPlanTemplate[valueKey] = values[valueKey];
			});
			const result: boolean = await this.lessonPlanTemplateModule.saveLessonPlanTemplateData(this.currentAction);
			if (result) {
				this.onCloseEditor();
			}
		};
		const error: (errors: any, values: any) => void = (errors, values) => {
			console.log(errors, values);
		};
		(this.$refs.templateEditorContent as TemplateEditorContentType).onSubmitEditorForm(success, error);
	}

	onDeleteLessonPlanTemplate(record: ILessonPlanTemplateModel): void {
		Modal.confirm({
			title: fmtMsg(LessonPlanLocale.LessonPlanTemplateDeletePromptMessage),
			okText: fmtMsg(CommonLocale.CommonYesButtonText),
			okType: "danger",
			cancelText: fmtMsg(CommonLocale.CommonNoButtonText),
			onOk: () => {
				this.lessonPlanTemplateModule.deleteLessonPlanTemplate(record);
			}
		});
	}
}
